import { zodResolver } from "@hookform/resolvers/zod";
import type { Table } from "@tanstack/react-table";
import { UploadIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { type IntlShape, useIntl } from "react-intl";
import { z } from "zod";

import { FormSelectSingle } from "../customFormFields/FormSelect";
import FormDialog from "../forms/FormDialog";

import { wpxTableMessages } from "./wpxTable.intl";

import { EXPORT_OPTS_OBJECT, ExportFileType } from "@/app/constants/constants";
import { ERROR_MSG } from "@/app/constants/error.constants";
import { handleDownloadTable } from "@/components/dataTable/utils/tableUtils";
import FormInput from "@/components/formFields/FormInput";
import { Button } from "@/components/ui/button";
import {
  DialogClose,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import { ExtendedColumnDef } from "@/interfaces/globals";

interface ExportManagerProps<TData, TValue> {
  columns: ExtendedColumnDef<TData, TValue>[];
  data: TData[];
  table?: Table<TData>;
  allowedOptions?: ExportFileType[];
}

/**
 * Creates file name validation schema with intl messages.
 * @param {IntlShape} intl - Intl instance used to resolve localized validation messages.
 * @returns {z.ZodType<string>} File name validation schema.
 */
const createValidateFileName = (intl: IntlShape): z.ZodType<string> =>
  z
    .string({
      invalid_type_error: intl.formatMessage(wpxTableMessages.invalidFileName),
      message: intl.formatMessage(wpxTableMessages.fileNameRequired),
    })
    .min(1, {
      message: intl.formatMessage(wpxTableMessages.fileNameRequired),
    })
    .max(50, {
      message: intl.formatMessage(wpxTableMessages.fileNameMaxLength),
    })
    .regex(
      /^[a-zA-Z0-9_\-.\s]+$/,
      intl.formatMessage(wpxTableMessages.fileNameInvalidChars),
    )
    .refine(
      (name) => !name.startsWith("."),
      intl.formatMessage(wpxTableMessages.fileNameCannotStartWithDot),
    )
    .refine(
      (name) => !name.endsWith("."),
      intl.formatMessage(wpxTableMessages.fileNameCannotEndWithDot),
    );

/**
 * Creates the export table form schema with intl messages.
 * @param {IntlShape} intl - Intl instance used to resolve localized validation messages.
 * @returns {z.ZodObject} Export form schema.
 */
const createExportTableSchema = (
  intl: IntlShape,
): z.ZodObject<{
  fileName: z.ZodType<string>;
  fileType: z.ZodNativeEnum<typeof ExportFileType>;
}> =>
  z.object({
    fileName: createValidateFileName(intl),
    fileType: z.nativeEnum(ExportFileType, {
      message: ERROR_MSG.SELECT_FILE_TYPE,
    }),
  });

/**
 * Export dialog for downloading table data in various formats.
 *
 * @param {ExportManagerProps<TData, TValue>} props - Component props.
 * @returns {React.JSX.Element} Rendered export dialog.
 */
const ExportManager = <TData, TValue>(
  props: ExportManagerProps<TData, TValue>,
): React.JSX.Element => {
  const { columns, data, table, allowedOptions } = props;
  const intl = useIntl();
  const exportTableSchema = useMemo(
    () => createExportTableSchema(intl),
    [intl],
  );
  type ExportTableSchemaValues = z.infer<typeof exportTableSchema>;
  const form = useForm<ExportTableSchemaValues>({
    resolver: zodResolver(exportTableSchema),
    mode: "onChange",
    values: {
      fileName: "",
      fileType: ExportFileType.XLSX,
    },
  });
  const [isOpen, setIsOpen] = useState(false);
  const handleExit = (): void => {
    setIsOpen(!isOpen);
    form.reset();
  };

  const onSubmit: SubmitHandler<ExportTableSchemaValues> = async (
    formValues,
  ) => {
    await handleDownloadTable<TData, TValue>({
      columns,
      data,
      table: table as Table<TData>,
      file: {
        name: formValues.fileName,
        type: formValues.fileType,
        sheet: "Sheet 1",
      },
    });
    handleExit();
  };

  const exportOptions = useMemo(() => {
    if (allowedOptions) {
      return EXPORT_OPTS_OBJECT.filter((opt) =>
        allowedOptions.includes(opt.value),
      );
    }

    return EXPORT_OPTS_OBJECT;
  }, [allowedOptions]);

  return (
    <FormDialog
      title={intl.formatMessage(wpxTableMessages.saveFile)}
      isOpen={isOpen}
      toggleModal={() => handleExit()}
      className='max-w-[300px] border'
      trigger={
        <DialogTrigger
          asChild
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen(true);
          }}
        >
          <div className='relative flex align-bottom select-none rounded-sm px-3 py-2 text-sm items-center outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 hover:bg-secondary cursor-pointer '>
            <UploadIcon className='mr-2 h-4 w-4' />
            <span>{intl.formatMessage(wpxTableMessages.export)}</span>
          </div>
        </DialogTrigger>
      }
    >
      <Form {...form}>
        <form
          id='export-form'
          onSubmit={form.handleSubmit(onSubmit)}
          className='space-y-7 3xl:space-y-9'
        >
          <FormInput
            form={form}
            name='fileName'
            label={intl.formatMessage(wpxTableMessages.fileName)}
            required
            input={{
              placeholder: intl.formatMessage(wpxTableMessages.enterFileName),
            }}
          />

          <FormInput
            form={form}
            name='fileType'
            label={intl.formatMessage(wpxTableMessages.fileType)}
            required
            render={(field) => (
              <FormSelectSingle
                placeholder={intl.formatMessage(
                  wpxTableMessages.selectFileType,
                )}
                field={field}
                className='w-full '
                options={exportOptions}
              />
            )}
          />
        </form>
      </Form>

      <DialogFooter className='mt-4'>
        <Button type='submit' form='export-form'>
          {intl.formatMessage(wpxTableMessages.export)}
        </Button>
        <DialogClose asChild>
          <Button className='text-primary' variant='ghost'>
            {intl.formatMessage(wpxTableMessages.cancel)}
          </Button>
        </DialogClose>
      </DialogFooter>
    </FormDialog>
  );
};

export default ExportManager;
